import React from 'react';
import { Table, Tag, Typography } from 'antd';

import {
	changeKind,
	describeRuleChange,
	isEntryChange,
	isEntryMove,
	formatChangeValue,
	historyTagEntries,
	historyTagRef,
	isRuleSummaryChange,
	isTagArrayChange,
	isTagSummaryChange,
	TAG_ARRAY_LABELS,
	tagSummaryKind,
	tagSummarySegments,
} from '../../utils/history';
import { RESTRICTED_CHANGE_TEXT } from '../../utils/elementAccess';
import { HistoryCard, HistoryTag, HistoryTagRow } from './HistoryTagBadges';
import MaskedValue from '../common/MaskedValue';

const { Text } = Typography;

/**
 * The field-level diff of one history entry as a Field · Before · After
 * table. Labels come from the API (the element's singular label, the
 * product word for a settings field, a member address like
 * Element "Mood" · Type); values render through formatChangeValue so
 * dates follow the display preference and empties read as a dash.
 *
 * `selectable` adds row selection — the revert modal uses it to pick a
 * subset of paths.
 *
 * A `conversion` entry (a card's number <-> measurement type change ran a
 * backfill of the stored item values) is prose, not a diff: its
 * `description` — the API's sentence — spans the Before/After cells, and it
 * is never selectable (a partial revert naming it is 400 not_revertable).
 *
 * A `hiddenValues` entry is prose too: a card's purge of the values its
 * items held under removed elements (`card.purgeValues`), or the revert
 * that put them back (`card.restoreValues`) — delete audit F9. It carries
 * `{ op, elements, items, values }` and the API's `description`, no
 * from/to. It is never selected on its own: the row is reverted WHOLE.
 *
 * An automatic tag's `ruleEdit` / `rulePass` entry (T6a) is prose too: the
 * rule set, changed or stopped, or what its pass did — counts only. So are
 * the tags' other summaries (utils/history.js tagSummarySegments): a tag's
 * delete or restore, a merge (the tag it went into as its badge), an item
 * tag's conversion, a card's Use items as tags, Apply where referenced and
 * its pass (the card as its record badge).
 *
 * TAGS ARE TAG BADGES (ruled b54): an item's `tagIds` / `tagAutoIds` sides
 * are rows of badges — named by `tagIndex` (the reader's vocabulary, a Map
 * id → tag), else by the API's live name beside each id — each the door to
 * its tag page where `linkTags`; a tombstone never is (HistoryTagBadges).
 * `tagsLoaded` says the vocabulary has answered, so a merge's survivor it
 * lacks is gone.
 */
export const isConversionChange = change => !!(change && change.conversion);
/**
 * A change the reader may not see (keepr-api § History): `restrictedElement`
 * — an element a rule hides from them (owner ruling e05, KPR-506) — or
 * `hiddenElement`, a value under an element the card no longer declares
 * (delete audit F9). Either way ONE sentence, no name, and never a value:
 * the row is drawn from the flag alone, so a from/to that ever rode along on
 * one (an older server, a bug) still never reaches the screen (KPR-509).
 */
export const isRestrictedChange = change => !!(change && (change.restrictedElement === true || change.hiddenElement === true));
const restrictedText = change => {
	if (change?.restrictedElement === true) return RESTRICTED_CHANGE_TEXT;
	const said = `${change?.description || ''}`.trim();
	return said ? `${said.charAt(0).toUpperCase()}${said.slice(1)}` : 'A removed field changed';
};
export const isHiddenValuesChange = change => !!(change && change.hiddenValues);
/** A manager's download of who confirmed (KPR-440): `{ rows }` and the API's sentence, no from/to. */
export const isConfirmationsExportChange = change => !!(change && change.confirmationsExport);
/** An automatic tag's rule edit or pass (T6a): counts said as a sentence (utils/history.js). */
export const isRuleProseChange = change => isRuleSummaryChange(change);
/** A change that reads as a sentence rather than a Before/After pair. */
// Only the RULE's restricted change is prose for selection: a removed
// element's (`hiddenElement`) stays pickable in a partial revert, as it was
// before element rules (it is drawn as a sentence all the same).
export const isProseChange = change => change?.restrictedElement === true || isConversionChange(change) || isHiddenValuesChange(change) || isConfirmationsExportChange(change) || isRuleProseChange(change) || isTagSummaryChange(change);
/**
 * A change drawn as ONE sentence across Before and After: the prose above,
 * and a repeating group's move (keepr-api KPR-678: "moved Ingredient 3 to
 * become Ingredient 1"). Unlike prose, a move stays selectable — reverting it
 * puts back just the entries that moved.
 */
export const isSentenceChange = change => isProseChange(change) || (isEntryMove(change) && Boolean(change.description));

/** The row key: the path, or the path suffixed for a prose entry so it never collides with the type change beside it. */
export const changeRowKey = (change, index) => {
	if (isRestrictedChange(change)) return `${change.path || 'elements'}#restricted-${index}`;
	if (isConversionChange(change)) return `${change.path || `change-${index}`}#conversion`;
	if (isHiddenValuesChange(change)) return `${change.path || `change-${index}`}#hiddenValues`;
	if (isConfirmationsExportChange(change)) return `${change.path || `change-${index}`}#confirmationsExport`;
	// The rule's summary shares the path `rule` with the rule's own diff.
	if (isRuleProseChange(change)) return `${change.path || `change-${index}`}#${change.ruleEdit ? 'ruleEdit' : 'rulePass'}`;
	if (isTagSummaryChange(change)) return `${change.path || `change-${index}`}#${tagSummaryKind(change)}`;
	return change.path || `change-${index}`;
};

export default function ChangeTable({
	changes = [],
	selectable = false,
	selectedPaths = [],
	onSelectionChange,
	size = 'small',
	className = '',
	tagIndex = null,
	tagsLoaded = false,
	linkTags = true,
}) {
	const rows = (changes || []).map((change, index) => ({ key: changeRowKey(change, index), change }));
	const tags = { index: tagIndex, loaded: tagsLoaded, linked: linkTags };

	const columns = [
		{
			title: 'Field',
			dataIndex: 'change',
			key: 'field',
			// A restricted change is one sentence across the row: no field
			// name, no Before, no After.
			onCell: row => (isRestrictedChange(row.change) ? { colSpan: 3 } : {}),
			render: change => (isRestrictedChange(change) ? (
				<Text type="secondary" className="history-change__restricted">{restrictedText(change)}</Text>
			) : (
				<span className="history-change__label" title={change.path}>
					{isTagArrayChange(change) ? TAG_ARRAY_LABELS[change.path] : (change.label || change.path)}
					{change.labelMissing && !isTagArrayChange(change) ? <Tag className="history-change__flag">no longer exists</Tag> : null}
				</span>
			)),
		},
		{
			title: 'Before',
			dataIndex: 'change',
			key: 'before',
			onCell: row => (isRestrictedChange(row.change) ? { colSpan: 0 } : isSentenceChange(row.change) ? { colSpan: 2 } : {}),
			render: change => (isRestrictedChange(change)
				? null
				: isProseChange(change)
				? <span className="history-change__conversion">{isTagSummaryChange(change) ? <TagSummary change={change} tags={tags} /> : (change.description || describeFallback(change))}</span>
				: isSentenceChange(change)
				? <span className="history-change__sentence">{sentenceCase(change.description)}</span>
				: renderSide(change, 'from', tags)),
		},
		{
			title: 'After',
			dataIndex: 'change',
			key: 'after',
			onCell: row => (isSentenceChange(row.change) ? { colSpan: 0 } : {}),
			render: change => (isRestrictedChange(change) || isSentenceChange(change) ? null : renderSide(change, 'to', tags)),
		},
	];

	return (
		<Table
			className={['history-changes', className].filter(Boolean).join(' ')}
			size={size}
			dataSource={rows}
			columns={columns}
			pagination={false}
			rowSelection={selectable ? {
				selectedRowKeys: selectedPaths,
				onChange: keys => (typeof onSelectionChange === 'function' ? onSelectionChange(keys) : undefined),
				getCheckboxProps: row => ({ disabled: isProseChange(row.change) }),
			} : undefined}
			scroll={{ x: 'max-content' }}
		/>
	);
}

/** The API's sentences start lower case (they sit inside a summary); a cell starts a sentence. */
function sentenceCase(text) {
	const said = `${text || ''}`.trim();
	return said ? `${said.charAt(0).toUpperCase()}${said.slice(1)}` : '';
}

/** An older entry without the described sentence: the summary's bare facts. */
function describeFallback(change) {
	if (isRuleProseChange(change)) return describeRuleChange(change);
	if (isConfirmationsExportChange(change)) {
		const rows = Number(change.confirmationsExport.rows) || 0;
		return `${rows} ${rows === 1 ? 'person' : 'people'}, as a file`;
	}
	if (isHiddenValuesChange(change)) {
		const h = change.hiddenValues || {};
		const names = Array.isArray(h.elements) && h.elements.length ? ` under ${h.elements.join(', ')}` : '';
		return `${h.op === 'restore' ? 'Restored' : 'Purged'} ${h.values ?? '?'} hidden values on ${h.items ?? '?'} items${names}`;
	}
	const c = change.conversion || {};
	return `${c.element || change.path} converted to ${c.to || '?'}${c.converted !== undefined ? `: ${c.converted} items converted` : ''}`;
}

/** A tag summary as its sentence: the card as its record badge, a merge's survivor as its tag badge. */
function TagSummary({ change, tags }) {
	return tagSummarySegments(change).map((segment, index) => {
		const key = `segment-${index}`;
		if (typeof segment === 'string') return <React.Fragment key={key}>{segment}</React.Fragment>;
		if (segment.card !== undefined) return <HistoryCard key={key} cardId={segment.card} />;
		if (segment.tag) {
			const ref = historyTagRef(segment.tag.id, segment.tag.name, tags.index, { loaded: tags.loaded });
			return <HistoryTag key={key} entry={ref} linked={tags.linked} />;
		}
		return null;
	});
}

function renderSide(change, side, tags = {}) {
	const kind = changeKind(change);
	if (kind === 'redacted') return <Text type="secondary" italic>withheld</Text>;
	if (isTagArrayChange(change)) {
		const entries = historyTagEntries(change[side], side === 'from' ? change.fromDisplay : change.toDisplay, tags.index);
		if (!entries.length) return <Text type="secondary">—</Text>;
		return <HistoryTagRow entries={entries} linked={tags.linked !== false} label={side === 'from' ? 'Tags before' : 'Tags after'} />;
	}
	if (kind === 'reorder') {
		const seq = change[side];
		return <Text type="secondary">{Array.isArray(seq) ? seq.join(' · ') : '—'}</Text>;
	}
	// A repeating group's entry added or removed (KPR-678) carries a one-line
	// summary of its values (`toDisplay` / `fromDisplay`) beside the tag. One
	// that holds a MASKED column's value (keepr-api `masked`, KPR-1353) is
	// drawn behind the mask, with Show and Copy: the summary is the API's
	// plain reading, so it is never printed as it is.
	const entrySummary = (display) => {
		if (!isEntryChange(change) || !display) return null;
		if (change.masked === true) {
			const noun = `${change.label || 'entry'} ${side === 'from' ? 'removed' : 'added'}`;
			return <span className="history-change__value history-change__entry"><MaskedValue value={`${display}`} text={`${display}`} label={noun} /></span>;
		}
		return <span className="history-change__value history-change__entry">{`${display}`}</span>;
	};
	if (kind === 'added') return side === 'from' ? <Text type="secondary">—</Text> : <><Tag color="green">added</Tag>{entrySummary(change.toDisplay)}</>;
	if (kind === 'removed') return side === 'to' ? <Text type="secondary">—</Text> : <><Tag color="red">removed</Tag>{entrySummary(change.fromDisplay)}</>;
	const value = change[side];
	const display = side === 'from' ? change.fromDisplay : change.toDisplay;
	const text = formatChangeValue(value, display);
	if (text === '—') return <Text type="secondary">—</Text>;
	// A masked element's change (keepr-api `masked`, KPR-1348): both sides
	// behind the mask, each with its own Show (and Copy). Nothing is recorded.
	if (change.masked === true) {
		return <span className="history-change__value"><MaskedValue value={value} text={text} label={`${change.label || 'value'} ${side === 'from' ? 'before' : 'after'}`} /></span>;
	}
	return <span className="history-change__value">{text}</span>;
}
